/**
 * Holds the signed-in session and the claims that come with it.
 *
 * The `loading` flag matters more than it looks. Firebase resolves the current
 * user asynchronously on every page load, and during that window the user is
 * neither signed in nor signed out. Rendering routes before it settles bounces
 * a signed-in user to the sign-in page for a frame and then back — which, on a
 * slow ward workstation, is long enough to click.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { onAuthStateChanged, signOut as fbSignOut, type User } from 'firebase/auth';
import { auth } from '../lib/firebase';
import { isRole, type Role } from '../types/roles';
import { AuthContext, type Session } from './AuthContext';

/**
 * The roles a token carries.
 *
 * Organization roles arrive as a `roles` array. A system administrator is
 * marked differently — `isSysAdmin: true`, set by the sysadmin tooling, with
 * no roles array at all — and reading only the array made a sysadmin look
 * like an account with no roles: every door led to the customer app, and
 * the console said "not a system administrator". Both shapes are read.
 */
export function rolesFromClaims(claims: Record<string, unknown>): Role[] {
  const raw = claims.roles;
  const roles: Role[] = Array.isArray(raw) ? raw.filter(isRole) : [];
  if (claims.isSysAdmin === true && !roles.includes('sysadmin')) roles.push('sysadmin');
  return roles;
}

async function readSession(user: User): Promise<Session> {
  const token = await user.getIdTokenResult();
  const roles = rolesFromClaims(token.claims as Record<string, unknown>);
  const orgId = typeof token.claims.orgId === 'string' ? token.claims.orgId : null;

  // The profile name is what the person typed on Settings; the token's
  // `name` claim is the same value as the server last saw it.
  const claimName = typeof token.claims.name === 'string' && token.claims.name.trim() ? token.claims.name.trim() : null;
  return {
    uid: user.uid,
    email: user.email,
    displayName: user.displayName || claimName,
    roles,
    orgId,
    platformSysAdmin: token.claims.isSysAdmin === true,
  };
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState<Session | null>(null);

  useEffect(() => {
    return onAuthStateChanged(auth, async (user) => {
      setSession(user ? await readSession(user) : null);
      setLoading(false);
    });
  }, []);

  const refresh = useCallback(async () => {
    const user = auth.currentUser;
    if (!user) return;
    // The cached profile (display name) only changes on reload, and the
    // cached token predates any claims changed server-side.
    await user.reload();
    await user.getIdToken(true);
    setSession(await readSession(user));
  }, []);

  const signOut = useCallback(async () => {
    await fbSignOut(auth);
  }, []);

  const value = useMemo(
    () => ({ loading, session, signOut, refresh }),
    [loading, session, signOut, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
